'use client';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import { getLanguage } from '@unifyapps/network/utils/native/localStorage';
import { isNative } from '@unifyapps/ui/utils/isNative';
import { useMemo } from 'react';
import { debugSSR } from '../../../../../../consts/debug';
import InterfaceHelper from '../../../../../stores/InterfaceStore/helper/InterfaceHelper';
import { useGetUserContext } from '../../../../useGetUserContext';
import type { UseFetchResourcesHookType } from '../../../types';
import { useGetAuthDeployedComponent } from '../useAuthDeployedComponent';
import {
  getAuthInterfaceComponentSlug,
  processAuthComponentData,
} from './utils/processComponentData';

type PageParams = Parameters<UseFetchResourcesHookType>[0];

interface FromRecordArgs {
  interfaceRecord: InterfaceEntity | undefined;
  isLoadingInterfaceRecord: boolean;
  errorInterfaceRecord: unknown;
  interfaceComponent: PageParams['interfaceComponent'];
  mode: PageParams['mode'];
}

/**
 * Shared tail for the Authorization / PlatformAuthorization page hooks. Given an already-resolved
 * interface record — governance resolves it by domain/host, platform-auth by id — this derives the
 * component slug + locale and fetches/processes the deployed component. Record resolution is the
 * ONLY thing the two strategies differ on, so it stays in each hook and everything downstream lives
 * here (no duplication, no `isPlatformAuthPage` branch).
 */
export function useAuthPageResourcesFromRecord({
  interfaceRecord,
  isLoadingInterfaceRecord,
  errorInterfaceRecord,
  interfaceComponent,
  mode,
}: FromRecordArgs) {
  const { userContext } = useGetUserContext();

  const interfaceComponentSlug = getAuthInterfaceComponentSlug(
    interfaceComponent,
    InterfaceHelper.getPublicPageIds(interfaceRecord),
  );

  const userLocale = isNative() ? getLanguage() : userContext?.locale;
  const locale = InterfaceHelper.getLocale({ userLocale, interfaceRecord, mode });
  const interfaceDefaultLocale = InterfaceHelper.getDefaultLocale(interfaceRecord);
  const includeNamespace = Boolean(interfaceDefaultLocale && interfaceDefaultLocale !== locale);

  const authComponent = useGetAuthDeployedComponent(interfaceComponentSlug, includeNamespace, {
    query: {
      enabled: Boolean(interfaceComponentSlug),
    },
  });

  const { fetchedComponent, dataSourcesMap, interfacePageNamespace } = useMemo(
    () => processAuthComponentData(authComponent.data, interfaceComponent, locale),
    [authComponent.data, interfaceComponent, locale],
  );

  const _isLoading = authComponent.isLoading || isLoadingInterfaceRecord;
  const _error = authComponent.error || errorInterfaceRecord;

  debugSSR(
    'useAuthPageResourcesFromRecord',
    {
      isLoading: _isLoading,
      isLoadingAuthComponent: authComponent.isLoading,
      isLoadingInterfaceRecord,
    },
    {
      interfaceId: interfaceRecord?.id,
      pageId: fetchedComponent?.id,
    },
  );

  return {
    isLoading: _isLoading,
    error: _error,
    data: interfaceRecord,
    dataSources: dataSourcesMap,
    interfacePage: fetchedComponent,
    interfacePageNamespace,
  };
}
